import { IntlProvider } from 'react-intl';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { observer } from 'mobx-react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  EntityPermissionProvider,
  DataProvider,
  GlobalErrorHandler,
  DialogProvider,
  DateTimeProviderLocalization,
  SnackbarProvider,
  useStore,
  PageLoading,
  getSessionVersionToken,
} from '@ui-framework/index';
import LocalInstanceBanner from '@ui-framework/components/UI/LocalInstanceBanner';
import getDomain from '@utils/GetDomain';
import {
  apiClient,
  dataProvider as defaultDataProvider,
  Footer,
  Logo,
} from '@product-framework/index';
import dayjs from 'dayjs';
import { injectedRoutes } from './modules';
import getRouter from './product-framework/router/index';
import {
  ERROR_PAGE_TYPE,
  getErrorMessage,
} from './product-framework/pages/ErrorPage';
import ErrorDisplay from './product-framework/components/ErrorDisplay';
import { darkTheme, lightTheme } from './themes';
import { DEFAULT_LOCALE, getLocaleMessages } from './state/domain/Locale';

const bustPublicHtmlAssetsCache = () => {
  const sessionVersion = getSessionVersionToken();
  const links = document.querySelectorAll(
    'link[rel*="icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"], link[rel="manifest"]',
  );

  links.forEach(link => {
    if (link.href.includes('favicon-32x32')) {
      // eslint-disable-next-line no-param-reassign
      link.href = `/favicon-32x32.png?v=${sessionVersion}`;
    }
    if (link.href.includes('apple-touch-icon')) {
      // eslint-disable-next-line no-param-reassign
      link.href = `/apple-touch-icon.png?v=${sessionVersion}`;
    }
    if (link.href.includes('favicon-16x16')) {
      // eslint-disable-next-line no-param-reassign
      link.href = `/favicon-16x16.png?v=${sessionVersion}`;
    }
    if (link.href.includes('favicon.ico')) {
      // eslint-disable-next-line no-param-reassign
      link.href = `/favicon.ico?v=${sessionVersion}`;
    }
    if (link.href.includes('manifest.json')) {
      // eslint-disable-next-line no-param-reassign
      link.href = `/manifest.json?v=${sessionVersion}`;
    }
  });
};

const router = getRouter(injectedRoutes);

function App() {
  const store = useStore();
  const darkModeEnabled = store.domain.masterData.get()?.darkMode || false;
  const theme =
    darkModeEnabled && store.domain.appearance.get() === 'dark'
      ? darkTheme
      : lightTheme;

  const [messages, setMessages] = useState(null);

  const domain = getDomain(process.env.REACT_APP_API_HOST, 'api');

  const serviceDirectoryDomain = getDomain(
    process.env.REACT_APP_SERVICE_DIRECTORY_API_HOST,
    'api',
  );

  const serviceJobDomain = getDomain(
    process.env.REACT_APP_SERVICE_JOB_API_HOST,
    'api',
  );

  const notificationDomain = getDomain(
    process.env.REACT_APP_NOTIFICATION_API_HOST,
    'api',
  );

  const sxclServicesDomain = getDomain(
    process.env.REACT_APP_SXCL_SERVICES_API_HOST,
    'api',
  );

  const chatbotServicesDomain = getDomain(
    process.env.REACT_APP_CHATBOT_API_URL,
    'api',
  );

  const issuesTrackingDomain = getDomain(
    process.env.REACT_APP_ISSUES_TRACKING_API_HOST,
    'api',
  );

  const axiosWrapper = apiClient(domain, store.domain.locale);

  const axiosWrapperServiceDirectory = apiClient(
    serviceDirectoryDomain,
    store.domain.locale,
  );
  const axiosWrapperServiceJob = apiClient(
    serviceJobDomain,
    store.domain.locale,
  );

  const axiosWrapperNotification = apiClient(
    notificationDomain,
    store.domain.locale,
  );

  const axiosWrapperSxclServices = apiClient(
    sxclServicesDomain,
    store.domain.locale,
    { domainHeader: true },
  );

  const axiosWrapperChatbot = apiClient(
    chatbotServicesDomain,
    store.domain.locale,
  );

  const axiosWrapperIssuesTracking = apiClient(
    issuesTrackingDomain,
    store.domain.locale,
    { domainHeader: true },
  );

  const dataProviders = {
    default: defaultDataProvider(axiosWrapper),
    serviceJob: defaultDataProvider(axiosWrapperServiceJob),
    notification: defaultDataProvider(axiosWrapperNotification),
    sxclServices: defaultDataProvider(axiosWrapperSxclServices),
    chatbot: defaultDataProvider(axiosWrapperChatbot),
    serviceDirectory: defaultDataProvider(axiosWrapperServiceDirectory),
    issuesTracking: defaultDataProvider(axiosWrapperIssuesTracking),
  };

  const SnackbarProps = {
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    autoHideDuration: 5000,
  };

  async function loadLocales() {
    const localeModule = await getLocaleMessages(store.domain.locale.get());
    dayjs.locale(store.domain.locale.get());
    // Dynamic JSON imports wrap the payload in `default`.
    const localeMessages = localeModule?.default ?? localeModule;
    if (localeMessages) {
      setMessages(localeMessages);
    }
  }

  useEffect(() => {
    loadLocales();
    bustPublicHtmlAssetsCache();
  }, [store.domain.locale.locale]);

  const staticLoadingMessage = {
    en: 'Loading Language',
    es: 'Cargando idioma',
    fr: 'Chargement de la langue',
  };

  if (messages === null) {
    return (
      <PageLoading>
        {staticLoadingMessage[store?.domain?.locale?.locale || 'en']}
      </PageLoading>
    );
  }

  return (
    <IntlProvider
      locale={store.domain.locale.get()}
      defaultLocale={DEFAULT_LOCALE}
      messages={messages}>
      <DataProvider dataProviders={dataProviders}>
        <EntityPermissionProvider
          permissionApiUrl={`${domain}/sxcl/v1/permissions/config`}>
          <ThemeProvider theme={theme}>
            <GlobalErrorHandler
              Footer={Footer}
              ErrorDisplay={ErrorDisplay}
              ERROR_PAGE_TYPE={ERROR_PAGE_TYPE}
              getErrorMessage={getErrorMessage}
              Logo={Logo}>
              <CssBaseline />
              <LocalInstanceBanner />

              {messages && store.domain.locale.get() && (
                <DateTimeProviderLocalization
                  dateAdapter={AdapterDayjs}
                  adapterLocale={store.domain.locale.get()}>
                  <SnackbarProvider SnackbarProps={SnackbarProps}>
                    <DialogProvider>
                      <RouterProvider router={router} />
                    </DialogProvider>
                  </SnackbarProvider>
                </DateTimeProviderLocalization>
              )}
            </GlobalErrorHandler>
          </ThemeProvider>
        </EntityPermissionProvider>
      </DataProvider>
    </IntlProvider>
  );
}

export default observer(App);
